import { isDefined } from '../utils/helper';
import { t } from '../utils/localization/loc';
import { TelemetryEventMap } from './appInsights/eventMap';
import { Service } from './service';

export type HelpMenuCategory = 'help' | 'training' | 'resources' | 'supportId';

export type HelpMenuAction = {
    type: 'link';
    text: string;
    href: string;
    category?: HelpMenuCategory;
    customEvent?: keyof TelemetryEventMap;
    /**
     * The `order` property optional specifies the sort-order relative to built-in help actions.
     *
     * Built-in help actions are ordered as '0', '1', '2', so the following properties hold:
     * - Negative ordering values will be shown _before_ built-in options
     * - Higher (ie, 99) ordering values will be shown _after_ built-in options
     * - Undefined ordering values are treated as 'max', so they would also show at the bottom
     */
    order?: number;
} | {
    type: 'button';
    text: string;
    id: string;
    category?: HelpMenuCategory;
    customEvent?: keyof TelemetryEventMap;
    /**
     * The `order` property optional specifies the sort-order relative to built-in help actions.
     *
     * Built-in help actions are ordered as '0', '1', '2', so the following properties hold:
     * - Negative ordering values will be shown _before_ built-in options
     * - Higher (ie, 99) ordering values will be shown _after_ built-in options
     * - Undefined ordering values are treated as 'max', so they would also show at the bottom
     */
    order?: number;
} | {
    id: string;
    type: 'copy';
    category?: HelpMenuCategory;
    customEvent?: keyof TelemetryEventMap;
    text: string;
    /**
     * The `order` property optional specifies the sort-order relative to built-in help actions.
     *
     * Built-in help actions are ordered as '0', '1', '2', so the following properties hold:
     * - Negative ordering values will be shown _before_ built-in options
     * - Higher (ie, 99) ordering values will be shown _after_ built-in options
     * - Undefined ordering values are treated as 'max', so they would also show at the bottom
     */
    order?: number;
};

export namespace HelpMenuAction {
    export const sortOrder = ({ order }: HelpMenuAction) => {
        if (isDefined(order)) {
            return order;
        }

        return Number.MAX_VALUE;
    };
}

export const DefaultDocUrls = {
    'zh-cn': 'https://docs.uipath.com/zh-CN',
    'zh-tw': 'https://docs.uipath.com/zh-CN',
    fr: 'https://docs.uipath.com/fr',
    de: 'https://docs.uipath.com/de',
    ja: 'https://docs.uipath.com/ja',
    es: 'https://docs.uipath.com/es',
    'es-mx': 'https://docs.uipath.com/es',
    'pt-br': 'https://docs.uipath.com/pt-BR',
    'pt': 'https://docs.uipath.com/pt-BR',
    en: 'https://docs.uipath.com',
};

/**
 * Helper function that creates a `HelpMenuAction` 'link' for a particular service
 */
export const createLinkFromActiveService = (activeService: string, services: Service[] | undefined, locale: string): HelpMenuAction => {
    const defaultDocumentationLink: HelpMenuAction = {
        category: 'help',
        text: t('help-menu-documentation', locale),
        type: 'link',
        href: DefaultDocUrls[locale] || DefaultDocUrls.en,
        order: 0,
    };

    const candidateServices = services?.filter(s => s.id === activeService);

    if (!candidateServices || candidateServices.length === 0) {
        return defaultDocumentationLink;
    }

    const service = candidateServices[0];
    const documentationUrl = Service.documentationUrl(service);
    if (!documentationUrl) {
        return defaultDocumentationLink;
    }

    return {
        ...defaultDocumentationLink,
        text: Service.documentationTitle(service, locale),
        href: documentationUrl,
    };
};
